import { getAlertHistoryList } from "api/alertAPI";
import { AlertHistoryList } from "api/interfaces/alertInterface.interface";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "react-query";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import { useAppSelector } from "redux/hooks";
import {
  BasePagination,
  DefaultQueryParam,
  LEVEL_TYPE,
  SORT_DIRECTION,
} from "api/interfaces/commonInterface.interface";
import {
  createSearchParams,
  useLocation,
  useNavigate,
  useOutletContext,
} from "react-router-dom";
import TableButton from "components/atoms/buttons/TableButton";
import TableColumnFilterTitle from "components/blocks/table/TableColumnFilterTitle";
import Type from "components/atoms/text/labels/Type";
import * as mediaQuery from "components/MediaQuery";
import { getRecorderTimeFormat } from "utils/timeUtil";
import { AlertRecorerTypeFilter } from "utils/AlertUtil";
import { convertType } from "utils/functions";
import { isNull } from "lodash";

import RecorderTitle from "components/blocks/recorder/RecorderTitle";
import { TableColumnsType } from "antd";
import {
  changeSortOrderAntd,
  CommonTable,
  ExpandTableContainer,
  onSortAntd,
} from "components/atoms/table/AntdTable";
import { ColumnType } from "antd/es/table";
import { baseQueryInfo, useQueryKey } from "utils/queryUtil";
import { useMenu } from "hook/useMenu";
import AccountInfoLabel from "components/atoms/text/labels/AccountInfoLabel";
import useApiError from "hook/useApiError";
import NoAlertRule from "components/blocks/NoAlertRule";
import { useAlertRules } from "hook/useAlertRules";
import { ExpandableConfig } from "antd/es/table/interface";
import AlertExpandContainer from "./AlertExpandContainer";
import useStompDebouncedRefetch, {
  StompDateSignalKey,
} from "hook/useStompDebouncedRefetch";

export interface AlertHistoryContext {
  keyword: string;
  setAlertDetail: (detail: {
    system: number;
    disk: number;
    video: number;
  }) => void;
  setAlertTotal: (total: number) => void;
  setTitleComponent: (
    title: Array<{ label: string; onClick?: () => void }> | string
  ) => void;
}

const initialQueryRuleInfo = {
  ...baseQueryInfo,
  sortType: "mergedSystemName",
  sortDirection: SORT_DIRECTION.ASC,
  type: null,
};

export default function AlertHistoryListComponent(): JSX.Element {
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );

  const { keyword, setAlertDetail, setAlertTotal, setTitleComponent } =
    useOutletContext<any>();

  const navigate = useNavigate();
  const location = useLocation();
  const { queryNavigate, menuNavigation } = useMenu({});
  const { hasAlertRuleList, alertRuleQuery } = useAlertRules({});
  const { lang, notifyError } = useApiError();
  const [isPending, setIsPending] = useState<boolean>(true);
  const isMobile = mediaQuery.useIsMobile();
  const isBigScreen = mediaQuery.useIsBigScreen();
  useEffect(() => {
    const changeParam = new URLSearchParams(location.search);
    if (changeParam.size > 0) {
      setAlertHistoryRuleQueryInfo((info) => {
        return {
          ...info,
          pageNum: isNull(changeParam.get("pageNum"))
            ? 0
            : Number(changeParam.get("pageNum")),
          pageLimit: isNull(changeParam.get("pageLimit"))
            ? 20
            : Number(changeParam.get("pageLimit")),
          keyword: isNull(changeParam.get("keyword"))
            ? ""
            : (changeParam.get("keyword") as string),
          sortType: isNull(changeParam.get("sortType"))
            ? "mergedSystemName"
            : (changeParam.get("sortType") as string),
          sortDirection: isNull(changeParam.get("sortDirection"))
            ? SORT_DIRECTION.ASC
            : (changeParam.get("sortDirection") as SORT_DIRECTION),
          type: isNull(changeParam.get("type"))
            ? null
            : (changeParam.get("type") as string),
        };
      });
    } else {
      setAlertHistoryRuleQueryInfo({ ...initialQueryRuleInfo });
    }
  }, [location]);

  const [data, setData] = useState<AlertHistoryList[]>([]);

  useEffect(() => {
    setTitleComponent("Alert History");
  }, []);

  const [alertHistoryRuleQueryInfo, setAlertHistoryRuleQueryInfo] =
    useState<BasePagination>({
      ...initialQueryRuleInfo,
    });
  const queryKey = useQueryKey(alertHistoryRuleQueryInfo);

  const onClickAlertRecorderType = useCallback(
    (value: string | undefined) => {
      if (value !== undefined) {
        if (value === "all") {
          queryNavigate({
            to: location.pathname,
            queryInfo: {
              pageLimit: alertHistoryRuleQueryInfo.pageLimit,
              pageNum: 0,
              keyword: alertHistoryRuleQueryInfo.keyword as string,
              sortType: alertHistoryRuleQueryInfo.sortType,
              sortDirection:
                alertHistoryRuleQueryInfo.sortDirection as SORT_DIRECTION,
              type: null,
            },
          });
          // setAlertHistoryRuleQueryInfo((info) => {
          //   return { ...info, type: null, pageNum: 0 };
          // });
        } else {
          queryNavigate({
            to: location.pathname,
            queryInfo: {
              pageLimit: alertHistoryRuleQueryInfo.pageLimit,
              pageNum: 0,
              keyword: alertHistoryRuleQueryInfo.keyword as string,
              sortType: alertHistoryRuleQueryInfo.sortType,
              sortDirection:
                alertHistoryRuleQueryInfo.sortDirection as SORT_DIRECTION,
              type: value,
            },
          });
          // setAlertHistoryRuleQueryInfo((info) => {
          //   return { ...info, type: value as string, pageNum: 0 };
          // });
        }
      }
    },
    [
      alertHistoryRuleQueryInfo.keyword,
      alertHistoryRuleQueryInfo.pageLimit,
      alertHistoryRuleQueryInfo.sortDirection,
      alertHistoryRuleQueryInfo.sortType,
      location.pathname,
      queryNavigate,
    ]
  );
  const onHeaderCell = (column: ColumnType<AlertHistoryList>) => {
    return {
      onClick: () => {
        if (column.key) {
          setAlertHistoryRuleQueryInfo((info) => {
            return {
              ...info,
              sortType: column.key as string,
              sortDirection: onSortAntd(info.sortDirection),
              pageNum: 0,
            };
          });
        }
      },
    };
  };

  // alert history
  const columns: TableColumnsType<AlertHistoryList> = [
    {
      key: "type",
      title: (
        <TableColumnFilterTitle
          title="Type"
          filters={AlertRecorerTypeFilter}
          onClick={onClickAlertRecorderType}
          data-testid="alert-history-history-table-column-filter-title-click-alert-recorder-type"
        />
      ),

      render: (value, row) => (
        <Type
          type={convertType(row)}
          licenseType={row.licenseType}
          isCloudCam={row.isCloudCam}
        />
      ),

      width: isMobile ? 90 : 110,
    },
    {
      key: "mergedSystemName",
      title: "Recorder Title",
      render: (row) => (
        <RecorderTitle
          mergedSystemName={row.name}
          recorderName={row.recorderName}
          recorderCount={row.recorderCount}
        />
      ),

      //row.type === "Spectrum" ? row.name : row.recorderName,
      sorter: true,
      sortOrder: changeSortOrderAntd(
        alertHistoryRuleQueryInfo,
        "mergedSystemName"
      ),
      onHeaderCell: onHeaderCell,
      defaultSortOrder: "ascend",
      ellipsis: true,
    },
    {
      title: "Organization",
      render: (value, row) => (
        <AccountInfoLabel
          level={LEVEL_TYPE.EU}
          accountNumber={row.accountNumber}
          accountName={row.accountName}
          direction={isBigScreen ? "row" : "column"}
        />
      ),

      width: isMobile ? 100 : isBigScreen ? "20%" : "10%",
      hidden: selectedAccount.accountLevel === LEVEL_TYPE.EU,
    },
    {
      title: "System",
      render: (row) => row.systemCount || 0,
      align: "center",
      responsive: ["xl"],
      width: "7%",
    },
    {
      title: "Disk",
      render: (row) => row.diskCount || 0,
      align: "center",
      responsive: ["xl"],
      width: "7%",
    },
    {
      title: "Video",
      render: (row) => row.videoCount || 0,
      align: "center",
      responsive: ["xl"],
      width: "7%",
    },
    {
      title: "Last Alert",
      render: (row) =>
        row.date !== undefined
          ? getRecorderTimeFormat(row.date, selectedAccount, row.timezone, true)
          : "",
      responsive: ["md"],
      width: isBigScreen ? "20%" : 200,
    },
    // 마지막 버튼
    {
      title: "",
      render: (row) => (
        <span className="row-click-button table-button-wrapper">
          {row.recorderCount === 1 && (
            <TableButton
              label="View"
              onClick={() => onRowClick(row)}
              // disabled={
              //   row.systemCount + row.diskCount + row.videoCount === 0
              //     ? true
              //     : false
              // }
              data-testid="alert-history-history-table-button-row"
            />
          )}
        </span>
      ),

      align: "right",
      width: isMobile ? 30 : 100,
    },
  ];

  const onClickMerged = (row: AlertHistoryList) => {
    menuNavigation({
      to: `/alert/history/merged/${row.mergedSystemId}`,
    });
  };

  const onRowClick = (row: AlertHistoryList) => {
    if (row.recorderCount !== undefined && row.recorderCount !== 1) {
      onClickMerged(row);
    } else {
      queryNavigate({
        to: `/alert/history/detail`,
        queryInfo: {
          recorderId: row.recorderId,
          account: selectedAccount.accountId,
        } as unknown as DefaultQueryParam,
      });
    }
  };

  const alertHistoryQuery = useQuery(
    ["alertHistoryList", queryKey, selectedAccount.accountId],
    () =>
      getAlertHistoryList({
        accountId: selectedAccount.accountId,
        payload: alertHistoryRuleQueryInfo,
      }),
    {
      retry: 0,
      enabled: selectedAccount.accountId !== "",
      refetchOnWindowFocus: false,
      onSuccess: (res: any) => {
        if (res.error !== 0 && res.result === undefined) {
          setData([]);
          setAlertTotal(0);
          setAlertDetail({
            system: 0,
            disk: 0,
            video: 0,
          });
          setAlertHistoryRuleQueryInfo({
            ...initialQueryRuleInfo,
          });
        } else {
          setAlertTotal(res.result.total);
          setAlertDetail({
            system: res.result.systemCount || 0,
            disk: res.result.diskCount || 0,
            video: res.result.videoCount || 0,
          });
          setData(res.result.alertRule);
          setAlertHistoryRuleQueryInfo((info) => {
            return {
              ...info,
              ...res.page,
            };
          });
        }
      },
      onError: (e: any) => {
        setData([]);
        setAlertTotal(0);
        setAlertDetail({
          system: 0,
          disk: 0,
          video: 0,
        });
        setAlertHistoryRuleQueryInfo({
          ...initialQueryRuleInfo,
        });
        if (e.response?.data !== undefined) {
          if (e.response?.data.error !== 5001) {
            notifyError(lang.label.alertHistory.notify.search.fail);
          }
        }
      },
      onSettled: (e: any) => {
        setIsPending(false);
      },
    }
  );

  const onChangePage = (page: number, totalRows: number) => {
    if (page !== alertHistoryRuleQueryInfo.pageNum + 1) {
      queryNavigate({
        to: location.pathname,
        queryInfo: {
          pageLimit: alertHistoryRuleQueryInfo.pageLimit,
          pageNum: page - 1,
          keyword: alertHistoryRuleQueryInfo.keyword as string,
          sortType: alertHistoryRuleQueryInfo.sortType,
          sortDirection:
            alertHistoryRuleQueryInfo.sortDirection as SORT_DIRECTION,
        },
      });
      setIsPending(true);
    }
  };
  useStompDebouncedRefetch({
    stompKey: ["alertListUpdated"] as StompDateSignalKey[],
    refetch: alertHistoryQuery.refetch,
  });

  useEffect(() => {
    setAlertHistoryRuleQueryInfo((query) => {
      return {
        ...query,
        keyword,
      };
    });
  }, [keyword]);

  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);

  const expandableProps = useMemo<ExpandableConfig<AlertHistoryList>>(() => {
    return {
      expandRowByClick: true,
      expandedRowKeys,
      onExpand: (expanded: boolean, row: AlertHistoryList) => {
        if (expanded) {
          setExpandedRowKeys([row.mergedSystemId]);
        } else {
          setExpandedRowKeys([]);
        }
      },
      expandedRowRender: (row: AlertHistoryList) => (
        <AlertExpandContainer
          mergedSystemId={row.mergedSystemId}
          queryKey={queryKey as BasePagination}
          columns={columns}
          onRowClick={onRowClick}
        />
      ),

      rowExpandable: (row: AlertHistoryList) => row.recorderCount > 1,
    };
  }, [columns, expandedRowKeys, queryKey]);

  if (!hasAlertRuleList && !alertRuleQuery.isLoading) {
    return (
      <div style={{ paddingTop: 50 }}>
        <NoAlertRule />
      </div>
    );
  }
  return (
    <ExpandTableContainer>
      <CommonTable
        rowKey={(row: AlertHistoryList) => row.mergedSystemId}
        columns={columns}
        dataSource={data}
        queryInfo={alertHistoryRuleQueryInfo}
        onChangePage={onChangePage}
        loading={isPending}
        expandable={expandableProps}
      />
    </ExpandTableContainer>
  );
}
